import orderBy from 'lodash/orderBy'
import { observer } from 'mobx-react'
import { useMemo } from 'react'

import { CheckboxBasedFilter, OptionsProps } from '@avantstay/backoffice-core'

import { SubcategoryProps } from '@new/domains/operations/components/NewFormFields/CaseInputsSearch/CaseInputsSearch.types'
import { casesFilterOptionsStore } from '@new/domains/operations/screens/CasesV2/stores/CasesFilterOptionsStore'
import { casesFiltersStore } from '@new/domains/operations/screens/CasesV2/stores/CasesFiltersStore'
import { casesStore } from '@new/domains/operations/screens/CasesV2/stores/CasesStore'
import { CaseFilterIds } from '@new/domains/operations/screens/CasesV2/utils/config'
import {
  buildTaxonomySubcategoryOptions,
  oldTaxonomyGroup,
  TaxonomyFilterLevel,
} from '@new/domains/operations/screens/CasesV2/utils/taxonomyFilterOptions'
import { parseLabel } from '@new/domains/operations/utils/parseLabel'

import * as S from './commonFilterStyles.style'
import { useTaxonomyColumnFilter } from './useTaxonomyColumnFilter'

const _SubcategoriesFilter = () => {
  const { caseInputs, isLoadingCaseInputs } = casesStore
  const { taxonomyTasks, isLoadingTaxonomyTasks } = casesFilterOptionsStore
  const {
    recordTypes,
    taxonomyRecordTypes,
    taxonomyTypes,
    taxonomyCategories,
    subcategories,
    taxonomySubcategories,
    shouldShowLegacyFilterOptions,
    shouldShowTaxonomyFilterOptions,
  } = casesFiltersStore

  const taxonomyOptions = useMemo(
    () =>
      shouldShowTaxonomyFilterOptions
        ? orderBy(
            buildTaxonomySubcategoryOptions(taxonomyTasks, {
              taskNodeIds: taxonomyRecordTypes,
              serviceTypeNodeIds: taxonomyTypes,
              categoryNodeIds: taxonomyCategories,
            }),
            ['group', 'label'],
          )
        : [],
    [
      taxonomyTasks,
      taxonomyRecordTypes,
      taxonomyTypes,
      taxonomyCategories,
      shouldShowTaxonomyFilterOptions,
    ],
  )

  const options = useMemo(() => {
    if (!caseInputs || !shouldShowLegacyFilterOptions) return taxonomyOptions
    const legacyOptions = Object.entries(caseInputs).reduce<OptionsProps<string>[]>(
      (acc, [recordType, caseInput]) => {
        if (!recordTypes || recordTypes.includes(recordType)) {
          const listByRecordType: OptionsProps<string>[] = []

          caseInput.caseTypes?.forEach(type => {
            type.categories?.forEach(categoryObj => {
              categoryObj.subcategories?.forEach((subcategoryObj: SubcategoryProps) => {
                listByRecordType.push({
                  label: parseLabel(subcategoryObj.label),
                  value: subcategoryObj.apiName,
                  group: oldTaxonomyGroup(
                    `${parseLabel(recordType)} - ${parseLabel(categoryObj.category.label)}`,
                  ),
                  hasFullWidth: true,
                })
              })
            })
          })
          return [...acc, ...listByRecordType]
        }
        return acc
      },
      [],
    )

    return [...taxonomyOptions, ...orderBy(legacyOptions, ['group', 'label'])]
  }, [caseInputs, recordTypes, taxonomyOptions, shouldShowLegacyFilterOptions])

  const { initialValue, handleDataFilter } = useTaxonomyColumnFilter({
    legacyFilterId: CaseFilterIds.Subcategory,
    level: TaxonomyFilterLevel.Subcategories,
    legacyValues: subcategories,
    taxonomyValues: taxonomySubcategories,
    taxonomyOptions,
  })

  return (
    <S.CheckBoxWrapper optionsLength={options.length}>
      <CheckboxBasedFilter
        shouldGroupBy
        isLoadingOptions={isLoadingCaseInputs || isLoadingTaxonomyTasks}
        title="Subcategories"
        options={options}
        initialValue={initialValue}
        shouldUpdateOnInitialValueChange
        onChange={handleDataFilter}
      />
    </S.CheckBoxWrapper>
  )
}

export const SubcategoriesFilter = observer(_SubcategoriesFilter)
